import React, { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material";
import { Square } from "@phosphor-icons/react";
import { useCategories } from "../../hooks/apiHooks/useCategories";
import {
  COLLAB_INTEREST_NSFW,
  COLLAB_INTEREST_SFW,
  CONTENT_INTEREST_NSFW,
  CONTENT_INTEREST_SFW,
} from "../../enums/generalEnums";

// Component for rendering SFW/NSFW toggle buttons
export const CategoryToggle = ({ showSFW, setShowSFW, categoryType }) => (
  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
    <Button
      fullWidth
      variant={showSFW ? "contained" : "outlined"}
      sx={{ color: showSFW ? "#FFF" : "inherit", minWidth: 100 }}
      onClick={() => setShowSFW(true)}
    >
      SFW
    </Button>
    <Button
      fullWidth
      variant={showSFW ? "outlined" : "contained"}
      sx={{ color: !showSFW ? "#FFF" : "inherit", minWidth: 100 }}
      onClick={() => setShowSFW(false)}
    >
      NSFW
    </Button>
  </Box>
);

// Component for rendering a category section (label, error, and toggle)
export const CategorySection = ({ title, error, showSFW, setShowSFW, categoryType }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xxs: "column", xsm: "row" },
      justifyContent: "space-between",
      alignItems: { xxs: "initial", xsm: "center" },
    }}
  >
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        {title} <span style={{ color: "#F96D82" }}>*</span>
      </Typography>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
    </Box>
    <CategoryToggle showSFW={showSFW} setShowSFW={setShowSFW} categoryType={categoryType} />
  </Box>
);

// Component for rendering checkboxes for a given category type
export const CheckboxGroup = ({ categories, selectedCategories, handleCheckboxChange, handleNotInterestedChange, categoryType }) => {
  const isNotInterestedChecked = selectedCategories.includes("");

  return (
    <FormGroup>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: {
            xxs: "repeat(2, 1fr)",
            xsm: "repeat(3, 1fr)",
            mds: "repeat(4, 1fr)",
          },
        }}
      >
        {categories.map((interest, index) => (
          <FormControlLabel
            key={index}
            label={interest.name}
            control={
              <Checkbox
                icon={<Square size={24} color="#919EAB" />}
                checked={selectedCategories.some((item) => item?.id === interest.id)}
                onChange={() => handleCheckboxChange(interest, categoryType)}
              />
            }
          />
        ))}
        <FormControlLabel
          label="Not Interested"
          control={
            <Checkbox
              icon={<Square size={24} color="#919EAB" />}
              checked={isNotInterestedChecked}
              onChange={(e) => handleNotInterestedChange(e.target.checked, categoryType)}
            />
          }
        />
      </Box>
    </FormGroup>
  );
};

// Main component
export const InterestMultiSelectCheckbox = ({ formik }) => {
  const [showCollabSFW, setShowCollabSFW] = useState(true);
  const [showContentSFW, setShowContentSFW] = useState(true);

  const {
    data: categories = [],
    isLoading,
    error,
  } = useCategories({
    type: [COLLAB_INTEREST_SFW, COLLAB_INTEREST_NSFW, CONTENT_INTEREST_NSFW, CONTENT_INTEREST_SFW],
  });

  // Group categories by type
  const groupedCategories = categories.reduce((acc, category) => {
    acc[category.type] = acc[category.type] || [];
    acc[category.type].push(category);
    return acc;
  }, {});

  const handleCheckboxChange = (interest, categoryType) => {
    let selectedCategories = formik.values[categoryType] || [];
    selectedCategories = selectedCategories.filter((item) => item !== "");
    const isSelected = selectedCategories.some((item) => item?.id === interest.id);
    const updated = isSelected
      ? selectedCategories.filter((item) => item.id !== interest.id)
      : [...selectedCategories, interest];
    formik.setFieldValue(categoryType, updated);
  };

  const handleNotInterestedChange = (checked, categoryType) => {
    if (checked) {
      formik.setFieldValue(categoryType, [""]);
    } else {
      const selectedCategories = formik.values[categoryType] || [];
      const updated = selectedCategories.filter((item) => item !== "");
      formik.setFieldValue(categoryType, updated);
    }
  };

  if (isLoading) {
    return <Typography>Loading categories...</Typography>;
  }

  if (error) {
    return <Typography color="error">Failed to load categories. Please try again.</Typography>;
  }

  return (
    <Box>
      <CategorySection
        title="Collab Interest"
        error={formik.errors.collabInterest}
        showSFW={showCollabSFW}
        setShowSFW={setShowCollabSFW}
        categoryType={showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW}
      />
      <CheckboxGroup
        categories={groupedCategories[showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW] || []}
        selectedCategories={formik.values[showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW] || []}
        handleCheckboxChange={handleCheckboxChange}
        handleNotInterestedChange={handleNotInterestedChange}
        categoryType={showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW}
      />

      <CategorySection
        title="Content Interest"
        error={formik.errors.contentInterest}
        showSFW={showContentSFW}
        setShowSFW={setShowContentSFW}
        categoryType={showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW}
      />
      <CheckboxGroup
        categories={groupedCategories[showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW] || []}
        selectedCategories={formik.values[showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW] || []}
        handleCheckboxChange={handleCheckboxChange}
        handleNotInterestedChange={handleNotInterestedChange}
        categoryType={showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW}
      />
    </Box>
  );
};
